import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Select,
  SelectItem,
} from '@heroui/react';
import _ from 'lodash';
import { type FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useAuth } from '@/app/hooks/auth-provider';
import { useFetchStaffMemberRolesQuery } from '@/slices/staff-role-api-slices.ts';
import { useSecurityContext } from '../hooks/security-provider.tsx';
import { filterRoles } from '../types/constants.ts';
import type { FormControlProps } from '../types/form-props.type.ts';

interface StaffRoleSelectProps extends FormControlProps {
  allowAllRoles?: boolean;
}

export const StaffRoleSelect: FC<StaffRoleSelectProps> = ({
  label = 'Permissions',
  displayProperty = 'description',
  name,
  disabled,
  required,
  allowAllRoles,
}) => {
  const [isDefinitionDialogOpen, setIsDefinitionDialogOpen] = useState(false);
  const { user } = useAuth();
  const { currentAccount } = useSecurityContext();
  const { roles, isLoading } = useFetchStaffMemberRolesQuery(null, {
    selectFromResult: ({ data, isLoading }) => ({
      roles: filterRoles(data, user, currentAccount, allowAllRoles),
      isLoading,
    }),
  });
  const { control } = useFormContext();

  return (
    <>
      <div className='flex items-center gap-2'>
        <div className='flex-1'>
          <Controller
            name={name}
            control={control}
            rules={{ required: required ? 'This field is required' : undefined }}
            render={({ field, fieldState }) => (
              <Select
                label={label}
                placeholder={`Select ${label}`}
                variant='bordered'
                isRequired={required}
                isDisabled={disabled || isLoading}
                isInvalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                items={roles || []}
                selectedKeys={field.value?.id ? new Set([field.value.id.toString()]) : new Set([])}
                onSelectionChange={keys => {
                  const selectedId = Array.from(keys)[0];
                  if (selectedId) {
                    const selectedRole = roles?.find(x => x.id === parseInt(selectedId as string));
                    field.onChange(selectedRole);
                  } else {
                    field.onChange(null);
                  }
                }}
              >
                {item => (
                  <SelectItem key={item.id.toString()} textValue={_.get(item, displayProperty)}>
                    {_.get(item, displayProperty)}
                  </SelectItem>
                )}
              </Select>
            )}
          />
        </div>
        <Button
          isIconOnly
          size='sm'
          variant='light'
          color='primary'
          aria-label='View permission role definitions'
          onPress={() => setIsDefinitionDialogOpen(true)}
        >
          <InformationCircleIcon className='h-5 w-5' />
        </Button>
      </div>

      <Modal
        isOpen={isDefinitionDialogOpen}
        onClose={() => setIsDefinitionDialogOpen(false)}
        placement='center'
        backdrop='blur'
        size='3xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>Permission Role Definitions</ModalHeader>
          <ModalBody className='pb-6'>
            {roles?.length &&
              roles.map(role => (
                <div key={role.id} className='flex flex-col gap-1'>
                  <span className='text-sm font-semibold'>{role.description}</span>
                  <span className='text-sm text-default-600'>{role.definition}</span>
                </div>
              ))}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
